/*
 * PROSOMO — product page: correct the VAT Dual Pricing app's "Membership price".
 *
 * Same problem, same fix as assets/prosomo-pcard-variant.js does for product cards, but the
 * product page needs its own variant hook, so it lives in its own file rather than bending the
 * card script (which is scoped to .sf__pcard) out of shape.
 *
 * The app (shopify://apps/vat-dual-pricing, eo_sh_storefront.js) computes its number ONCE per
 * page load as  price / (1 + pct)  — the VAT-extraction formula — instead of  price * (1 - pct).
 * At 15% that turns a $13.00 variant into $11.30 rather than $11.05. It also never follows the
 * selected variant. We keep the app's own label and markup and rewrite only the number inside
 * its span#apply-price-exclude.
 *
 * Variant changes come from the theme itself: assets/variant-picker.js ends its update with
 *     window.MinimogEvents.emit(`${this.productId}__VARIANT_CHANGE`, this.currentVariant, this)
 * where productId is the container's data-product-id, i.e. {{ product.id }}. Subscribing to that
 * hands us the variant object directly, so we never have to guess which [name="id"] input is the
 * authoritative one (the product page has more than one — the main form and the installment form).
 *
 * NOTE: this is a deliberate coupling to another app's DOM. If that app is removed or renames
 * #apply-price-exclude, the number simply stops being corrected; nothing else breaks.
 */
(function () {
  'use strict';

  var HOST = '[data-prosomo-product-membership]';
  var MEMBER_SPAN = '#apply-price-exclude';
  var DEFAULT_PERCENT = 15;

  function percentOf(host) {
    var raw = host.getAttribute('data-pp-member-percent');
    if (raw === null || raw === '') return DEFAULT_PERCENT;
    var n = Number(raw);
    // 0 is legitimate, so test explicitly rather than relying on falsiness.
    return isFinite(n) && n >= 0 && n < 100 ? n : DEFAULT_PERCENT;
  }

  function format(cents) {
    var symbol = (window.appCurrency && window.appCurrency.format) || '$';
    // Mirror the app's own "$ 11.05" spacing so the corrected value looks untouched.
    return symbol + ' ' + (cents / 100).toFixed(2);
  }

  // Returns true only when it actually changed the DOM, so the caller can budget real corrections.
  function write(host, priceCents) {
    if (typeof priceCents !== 'number' || !isFinite(priceCents)) return false;
    var span = document.querySelector(MEMBER_SPAN);
    if (!span) return false;
    var text = format(Math.round((priceCents * (100 - percentOf(host))) / 100));
    // Only write on a real change — this is what stops the observer below from feeding itself.
    if (span.textContent.trim() === text) return false;
    span.textContent = text;
    return true;
  }

  function start(host) {
    var currentPrice = Number(host.getAttribute('data-pp-initial-price'));
    var apply = function () { return write(host, currentPrice); };

    apply();

    /*
     * ---- Why an observer replaced the old 400ms / 1500ms re-asserts ----
     *
     * The app's span does not exist in the server HTML at all: eo_sh_storefront.js is deferred
     * from a CDN and injects #apply-price-exclude asynchronously, then computes its (wrong) value.
     * The previous version re-asserted our number at a fixed 0 / 400 / 1500ms and then gave up —
     * so whenever the app landed LATER than 1500ms (cold cache, slow connection, a busy page) its
     * value was the last write and the storefront showed the uncorrected price. That is a race we
     * cannot win with fixed delays, because there is no upper bound on when a third-party script
     * finishes.
     *
     * So: watch instead of guess. Any time anything changes in the document, re-check the span and
     * correct it if it is not already our value.
     *
     * This is NOT the "permanent observer fighting the app" the earlier design avoided:
     *   - write() only writes when the text actually DIFFERS, so once corrected we go quiet. Our
     *     own write re-enters the observer exactly once and then matches, ending the cycle.
     *   - the callback is rAF-coalesced, so a burst of hundreds of unrelated mutations costs one
     *     querySelector per frame, and nothing at all while the page is idle.
     *   - a write budget (below) disconnects us entirely if the app ever turns out to hold an
     *     opposing observer, so a pathological ping-pong self-limits instead of spinning forever.
     */
    var writes = 0;
    var MAX_WRITES = 50;
    var observer = null;
    var checkPending = false;

    function check() {
      if (apply()) {
        // Budget is per variant-selection cycle; a genuine correction storm means something is
        // writing back at us, and at that point stopping is better than burning the main thread.
        if (++writes >= MAX_WRITES && observer) observer.disconnect();
      }
    }

    function scheduleCheck() {
      if (checkPending) return;
      checkPending = true;
      window.requestAnimationFrame(function () {
        checkPending = false;
        check();
      });
    }

    if (window.MutationObserver) {
      observer = new window.MutationObserver(scheduleCheck);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    } else {
      // No MutationObserver: fall back to the old bounded re-asserts rather than nothing.
      window.setTimeout(apply, 400);
      window.setTimeout(apply, 1500);
    }

    var productId = host.getAttribute('data-pp-product-id');
    var tries = 0;
    (function subscribe() {
      var events = window.MinimogEvents;
      if (events && typeof events.subscribe === 'function') {
        events.subscribe(productId + '__VARIANT_CHANGE', function (variant) {
          if (variant && typeof variant.price === 'number') {
            currentPrice = variant.price;
            // New variant, new target value — reset the budget and re-arm if it had tripped.
            writes = 0;
            if (observer && window.MutationObserver) {
              observer.observe(document.documentElement, {
                childList: true,
                subtree: true,
                characterData: true
              });
            }
            apply();
          }
        });
        return;
      }
      // Both this file and the theme bundles are deferred, so MinimogEvents may not exist yet.
      if (++tries < 60) window.setTimeout(subscribe, 100);
    })();
  }

  function init() {
    var host = document.querySelector(HOST);
    if (host) start(host);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
